import { DateField } from "@/components/admin/date-field";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { SelectField } from "@/components/admin/select-field";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";
import { UrlField } from "@/components/admin/url-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Archive, Globe, Linkedin, Phone, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useDelete,
  useNotify,
  useRecordContext,
  useRedirect,
  useUpdate,
} from "ra-core";

import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SaleName } from "../sales/SaleName";
import type { Company } from "../types";
import { sizes } from "./sizes";

const SoftDeleteCompanyButton = () => {
  const record = useRecordContext<Company>();
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);

  const handleArchive = () => {
    update(
      "companies",
      { id: record!.id, data: { is_deleted: true }, previousData: record },
      {
        onSuccess: () => {
          notify("Company archived — data preserved for historical records");
          redirect("list", "companies");
        },
        onError: () => notify("Failed to archive company", { type: "error" }),
      }
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 cursor-pointer text-destructive border-destructive/40 hover:bg-destructive/10 hover:border-destructive"
        onClick={() => setOpen(true)}
      >
        <Archive className="w-3 h-3 mr-1.5" /> Archive Company
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive "{record?.name}"?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The company will be hidden from all lists. All linked quotations,
            invoices, PDC cheques, and other records are preserved and remain
            accessible in their respective modules.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleArchive}
              disabled={isPending}
            >
              {isPending ? "Archiving…" : "Archive Company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Permanent delete (ID:8). Companies are referenced (RESTRICT) by sales orders,
 * quotations, invoices etc., so a hard delete only succeeds for companies with
 * no linked records. On a FK violation we tell the user to archive instead,
 * which is the safe path that preserves history.
 */
const DeleteCompanyButton = () => {
  const record = useRecordContext<Company>();
  const [deleteOne, { isPending }] = useDelete();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);

  const handleDelete = () => {
    deleteOne(
      "companies",
      { id: record!.id, previousData: record },
      {
        onSuccess: () => {
          notify("Company deleted permanently");
          redirect("list", "companies");
        },
        onError: (error: any) => {
          const msg = String(error?.message || "");
          // Postgres FK violation (23503) surfaces via PostgREST 409.
          const isFkConflict =
            msg.includes("violates foreign key") ||
            msg.includes("still referenced") ||
            error?.status === 409;
          notify(
            isFkConflict
              ? "Cannot delete: this company has linked records (quotations, orders, invoices…). Archive it instead to preserve history."
              : "Failed to delete company",
            { type: "error", autoHideDuration: 6000 },
          );
          setOpen(false);
        },
      },
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 cursor-pointer text-destructive border-destructive/40 hover:bg-destructive/10 hover:border-destructive"
        onClick={() => setOpen(true)}
      >
        <Trash2 className="w-3 h-3 mr-1.5" /> Delete Permanently
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{record?.name}" permanently?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently removes the company. It will fail if the company has
            any linked quotations, orders, invoices, or other records — in that
            case use <strong>Archive</strong> instead. This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isPending}
            >
              {isPending ? "Deleting…" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 space-y-4">
      <div className="flex flex-row space-x-1">
        {link === "edit" ? (
          <ShowButton label="View Company" />
        ) : (
          <EditButton label="Edit Company" />
        )}
      </div>

      <CompanyInfo record={record} />

      <AddressInfo record={record} />

      <ContextInfo record={record} />

      <AdditionalInfo record={record} />

      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <SoftDeleteCompanyButton />
          <DeleteCompanyButton />
        </div>
      )}
    </div>
  );
};

export const CompanyInfo = ({ record }: { record: Company }) => {
  if (!record.website && !record.linkedin_url && !record.phone_number) {
    return null;
  }

  return (
    <AsideSection title="Company Info">
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-6">
          <Globe className="w-4 h-4" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <div className="flex flex-row items-center gap-1 min-h-6">
          <Linkedin className="w-4 h-4" />
          <a
            className="underline hover:no-underline"
            href={record.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            title={record.linkedin_url}
          >
            LinkedIn
          </a>
        </div>
      )}
      {record.phone_number && (
        <div className="flex flex-row items-center gap-1 min-h-6">
          <Phone className="w-4 h-4" />
          <TextField source="phone_number" />
        </div>
      )}
    </AsideSection>
  );
};

export const ContextInfo = ({ record }: { record: Company }) => {
  const { companySectors } = useConfigurationContext();
  if (!record.revenue && !record.id) {
    return null;
  }

  const sectorLabel = companySectors.find(
    (s) => s.value === record.sector,
  )?.label;

  return (
    <AsideSection title="Context">
      {sectorLabel && <span>Sector: {sectorLabel}</span>}
      {record.size && (
        <span>
          Size: <SelectField source="size" choices={sizes} />
        </span>
      )}
      {record.revenue && (
        <span>
          Revenue: <TextField source="revenue" />
        </span>
      )}
      {record.tax_identifier && (
        <span>
          Tax Identifier: <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

export const AddressInfo = ({ record }: { record: Company }) => {
  if (
    !record.address &&
    !record.city &&
    !record.zipcode &&
    !record.state_abbr
  ) {
    return null;
  }

  return (
    <>
      <AsideSection title="Main Address" noGap>
        {record.address && <TextField source="address" />}
        {(record.city || record.state_abbr || record.zipcode) && (
            <div className="text-sm">
                {[record.city, record.state_abbr, record.zipcode].filter(Boolean).join(", ")}
            </div>
        )}
        {record.country && <TextField source="country" />}
      </AsideSection>
      
      {record.billing_address && (
        <AsideSection title="Billing Address" noGap>
          <TextField source="billing_address" />
        </AsideSection>
      )}

      {record.shipping_address && (
        <AsideSection title="Shipping Address" noGap>
          <TextField source="shipping_address" />
        </AsideSection>
      )}
    </>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  if (
    !record.created_at &&
    !record.sales_id &&
    !record.description &&
    !record.context_links
  ) {
    return null;
  }
  const getBaseURL = (url: string) => {
    const urlObject = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObject.hostname;
  };

  return (
    <AsideSection title="Additional Info">
      {record.description && (
        <p className="text-sm  mb-1">{record.description}</p>
      )}
      {record.context_links && (
        <div className="flex flex-col">
          {record.context_links.map((link, index) =>
            link ? (
              <a
                key={index}
                className="text-sm underline hover:no-underline mb-1"
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                title={link}
              >
                {getBaseURL(link)}
              </a>
            ) : null,
          )}
        </div>
      )}
      {record.sales_id !== null && (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          Followed by&nbsp;
          <ReferenceField source="sales_id" reference="sales" record={record}>
            <SaleName />
          </ReferenceField>
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          Added on{" "}
          <DateField
            source="created_at"
            record={record}
            options={{
              year: "numeric",
              month: "long",
              day: "numeric",
            }}
          />
        </p>
      )}
    </AsideSection>
  );
};
